import * as React from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Warning24Regular, Info24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  dialog: {
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '200px',
  },
  title: {
    marginBottom: tokens.spacingVerticalS,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: tokens.spacingVerticalL,
    gap: tokens.spacingHorizontalM,
  },
  iconWarning: {
    fontSize: '48px',
    color: tokens.colorPaletteRedForeground1,
    flexShrink: 0,
  },
  iconInfo: {
    fontSize: '48px',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    flex: 1,
  },
  actions: {
    flexShrink: 0,
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
  },
});

interface ConfirmDialogProps {
  trigger?: React.ReactNode;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  intent?: 'primary' | 'danger';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  mode?: 'confirm' | 'alert'; // 'confirm' = dois botões, 'alert' = apenas OK
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  trigger,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  intent = 'primary',
  open,
  onOpenChange,
  mode = 'confirm',
}) => {
  const styles = useStyles();
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;

  const handleOpenChange = (_event: any, data: { open: boolean }) => {
    if (isControlled) {
      onOpenChange?.(data.open);
    } else {
      setInternalOpen(data.open);
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
    handleOpenChange({} as any, { open: false });
  };

  const handleCancel = () => {
    onCancel?.();
    handleOpenChange({} as any, { open: false });
  };

  const dialogContent = (
    <DialogSurface className={styles.dialog}>
      <DialogTitle className={styles.title}>{title}</DialogTitle>
      <DialogBody className={styles.body}>
        {mode === 'confirm' ? (
          <Warning24Regular className={styles.iconWarning} />
        ) : (
          <Info24Regular className={styles.iconInfo} />
        )}
        <div className={styles.message}>{message}</div>
      </DialogBody>
      <DialogActions className={styles.actions}>
        {mode === 'confirm' ? (
          <>
            <Button appearance="subtle" onClick={handleCancel}>
              {cancelText}
            </Button>
            <Button
              appearance={intent === 'danger' ? 'primary' : 'primary'}
              onClick={handleConfirm}
            >
              {confirmText}
            </Button>
          </>
        ) : (
          <Button
            appearance="primary"
            onClick={handleConfirm}
          >
            {confirmText || 'OK'}
          </Button>
        )}
      </DialogActions>
    </DialogSurface>
  );

  if (trigger) {
    return (
      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger disableButtonEnhancement>
          {React.isValidElement(trigger) ? trigger : <span>{trigger}</span>}
        </DialogTrigger>
        {dialogContent}
      </Dialog>
    );
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog>
  );
};

export default ConfirmDialog;